// @ts-nocheck
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import resolveEntryForPlant from "@store/Framework/Common/Utilities/resolveEntryForPlant";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import { LeadTimeEntryModel } from "@store/Framework/Types/ApiModels";
import { ProductLineComponentModel } from "@store/Library/Components/ProductLine/ProductLine";
import findLastIndex from "lodash/findLastIndex";

export const getProductLineTitle = (productLine: ProductLineComponentModel | undefined) => {
    return (
        productLine?.productTitle ||
        productLine?.shortDescription ||
        productLine?.description ||
        productLine?.productName
    );
};

export const getProductLineIsActive = (productLine: ProductLineComponentModel | undefined) => {
    return productLine?.isActiveProduct ?? productLine?.isActive ?? true;
};

export const getProductLineUrl = (productLine: ProductLineComponentModel | undefined, productInfo?: ProductInfo) => {
    return productInfo?.productDetailPath || productLine?.productUri;
};

export const getProductLineLineId = (productLine: ProductLineComponentModel | undefined) => {
    if (productLine && typeof productLine.productId !== "undefined") {
        return productLine.id;
    }

    return undefined;
};

export const getProductLineProductId = (
    productLine: ProductLineComponentModel | undefined,
    productInfo?: ProductInfo,
) => {
    if (productInfo?.productId) {
        return productInfo.productId;
    }

    if (!productLine) {
        return undefined;
    }

    if (typeof productLine.productId === "undefined") {
        // "productId" can be a "string" or "null", so we look for "undefined" intentionally
        // "productId" not being set means the line "id" is actually the product id (e.g.: ProductModel)
        return productLine.id;
    }

    return productLine.productId;
};

export const getProductLineProductNumber = (productLine: ProductLineComponentModel | undefined) => {
    if (!productLine) {
        return undefined;
    }

    return productLine.productNumber || productLine.erpNumber || productLine.productErpNumber;
};

export const minimumOrderQuantityThreshold = 1;

export const resolveProductLineMinimumOrderQuantity = (
    productLine: ProductLineComponentModel | undefined,
): number | undefined => {
    if (!productLine) {
        return undefined;
    }

    const pricing = productLine.pricing;

    const pricingMoq = Number(pricing?.additionalResults?.minimumOrderQuantity);

    if (pricingMoq) {
        return pricingMoq;
    }

    const plant = pricing?.additionalResults?.warehouseName || productLine.properties.warehouseName;

    const leadTimeEntryModel = resolveEntryForPlant<LeadTimeEntryModel>(
        productLine.properties.leadTimes,
        getProductLineProductNumber(productLine),
        plant,
    );

    return Number(leadTimeEntryModel?.MinimumOrderQuantity || productLine.properties.minimumOrderQuantity) || undefined;
};

/**
 * Product context flavour of "resolveProductLineMinimumOrderQuantity", for surfaces that work with
 * a product plus its loaded pricing (PDP, PLP, accessory upsell) instead of a line model.
 */
export const resolveProductMinimumOrderQuantity = (
    product: ProductModel | undefined,
    productInfo: ProductInfo | undefined,
): number | undefined => {
    if (!product) {
        return undefined;
    }

    return resolveProductLineMinimumOrderQuantity({ ...product, pricing: productInfo?.pricing });
};

export const hasMeaningfulMinimumOrderQuantity = (
    minimumOrderQuantity: number | undefined,
): minimumOrderQuantity is number =>
    minimumOrderQuantity !== undefined && minimumOrderQuantity > minimumOrderQuantityThreshold;

export const sortProductLines = <T extends ProductLineComponentModel>(productLines: T[] | null | undefined) => {
    return productLines?.reduce<T[]>((result, line) => {
        if (result.includes(line)) {
            return result;
        }

        const parentRefId = line.properties.parentRefId;

        if (parentRefId) {
            const parentIndex = result.findIndex(x => x.properties.refId === parentRefId);

            if (parentIndex !== -1) {
                const lastChildIndex = findLastIndex(result, x => x.properties.parentRefId === parentRefId);
                const sliceIndex = (lastChildIndex !== -1 ? lastChildIndex : parentIndex) + 1;

                return [...result.slice(0, sliceIndex), line, ...result.slice(sliceIndex)];
            }

            const parentLine = productLines.find(x => x.properties.refId === parentRefId);

            if (parentLine) {
                return [...result, parentLine, line];
            }
        }

        return [...result, line];
    }, []);
};
